/**
 * Calculates the adjugate matrix of a given 3x3 matrix.
 *
 * @param {number[]} matrix - The matrix to calculate the adjugate, in array form.
 * @returns {number[]} The adjugate matrix in array form.
 */
export const adjugate3x3Matrix = matrix => [
  matrix[4] * matrix[8] - matrix[5] * matrix[7],
  matrix[2] * matrix[7] - matrix[1] * matrix[8],
  matrix[1] * matrix[5] - matrix[2] * matrix[4],
  matrix[5] * matrix[6] - matrix[3] * matrix[8],
  matrix[0] * matrix[8] - matrix[2] * matrix[6],
  matrix[2] * matrix[3] - matrix[0] * matrix[5],
  matrix[3] * matrix[7] - matrix[4] * matrix[6],
  matrix[1] * matrix[6] - matrix[0] * matrix[7],
  matrix[0] * matrix[4] - matrix[1] * matrix[3]
];

/**
 * Calculates the matrix multiplication of two matrices given their dimensions.
 *
 * @param {object} arg - The arguments for the function.
 * @param {number[]} arg.matrixA - The first matrix to multiply, in array form.
 * @param {number[]} arg.matrixB - The second matrix to multiply, in array form.
 * @param {number} arg.rowsInA - The number of rows of the first matrix.
 * @param {number} arg.columnsInA - The number of columns of the first matrix.
 * @param {number} arg.rowsInB - The number of rows of the second matrix.
 * @param {number} arg.columnsInB - The number of columns of the second matrix.
 * @returns {number[]} The matrix result of the multiplication, in array form.
 */
export const multiplyMatrices = ({
  matrixA,
  matrixB,
  rowsInA,
  columnsInA,
  rowsInB,
  columnsInB
}) => {
  if (rowsInB !== columnsInA) throw Error('Dimensions mismatch');

  const matrixC = new Array(rowsInA * columnsInB);
  for (let i = 0; i < rowsInA; i += 1) {
    for (let j = 0; j < columnsInB; j += 1) {
      let matrixCij = 0;
      for (let k = 0; k < columnsInA; k += 1)
        matrixCij += matrixA[columnsInA * i + k] * matrixB[columnsInB * k + j];
      matrixC[columnsInB * i + j] = matrixCij;
    }
  }
  return matrixC;
};

/**
 * Calculates the change of basis matrix of a space given a set of 4 points that belong to it.
 *
 * @param {object} coordinates - The coordinates of the 4 points.
 * @param {number} coordinates.x1 - The first coordinate of the first point.
 * @param {number} coordinates.y1 - The second coordinate of the first point.
 * @param {number} coordinates.x2 - The first coordinate of the second point.
 * @param {number} coordinates.y2 - The second coordinate of the second point.
 * @param {number} coordinates.x3 - The first coordinate of the third point.
 * @param {number} coordinates.y3 - The second coordinate of the third point.
 * @param {number} coordinates.x4 - The first coordinate of the forth point.
 * @param {number} coordinates.y4 - The second coordinate of the forth point.
 * @returns {number[]} The matrix of the basis, in array form.
 */
export const calculateBasisFromPoints = ({ x1, y1, x2, y2, x3, y3, x4, y4 }) => {
  const componentMatrix = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
  const eigenVector = multiplyMatrices({
    matrixA: adjugate3x3Matrix(componentMatrix),
    matrixB: [x4, y4, 1],
    rowsInA: 3,
    columnsInA: 3,
    rowsInB: 3,
    columnsInB: 1
  });
  const flag =
    eigenVector[0] === eigenVector[1] && eigenVector[1] === eigenVector[2] && eigenVector[2] === 0;
  eigenVector[0] = flag ? 1 : eigenVector[0];
  eigenVector[1] = flag ? 1 : eigenVector[1];
  eigenVector[2] = flag ? 1 : eigenVector[2];
  const eigenVectorAsMatrix = [eigenVector[0], 0, 0, 0, eigenVector[1], 0, 0, 0, eigenVector[2]];
  return multiplyMatrices({
    matrixA: componentMatrix,
    matrixB: eigenVectorAsMatrix,
    rowsInA: 3,
    columnsInA: 3,
    rowsInB: 3,
    columnsInB: 3
  });
};

/**
 * Calculates the homography matrix between two spaces given a set of 4 points of each of them.
 *
 * @param {object} arg - The arguments object.
 * @param {object} arg.originPoints - The set of 4 points of the origin space.
 * @param {object} arg.destinationPoints - The set of 4 points of the destination space.
 * @returns {number[]} The matrix of the homography between the two spaces, in array form.
 */
export const calculateHomographyMatrix = ({ originPoints, destinationPoints }) => {
  const originBasis = calculateBasisFromPoints(originPoints);
  const destinationBasis = calculateBasisFromPoints(destinationPoints);
  const projectionMatrix = multiplyMatrices({
    matrixA: destinationBasis,
    matrixB: adjugate3x3Matrix(originBasis),
    rowsInA: 3,
    columnsInA: 3,
    rowsInB: 3,
    columnsInB: 3
  });
  const lastMatrixElement = projectionMatrix[8] !== 0 ? projectionMatrix[8] : 1;
  const normalizedProjectionMatrix = projectionMatrix.map(
    matrixElement => matrixElement / lastMatrixElement
  );
  return normalizedProjectionMatrix;
};

/**
 * Calculates the geographical coordinates corresponding to a point in the video.
 *
 * @param {object} arg - The arguments for the function.
 * @param {number[][]} arg.geoJSONCoordinates - The geographical coordinates of the 4 projected corners
 * of the video.
 * @param {number[]} arg.videoCoordinates - The video point coordinates in pixels.
 * @param {number} arg.videoTop - The video upper limit in pixels.
 * @param {number} arg.videoLeft - The video left limit in pixels.
 * @param {number} arg.videoWidth - The video width in pixels.
 * @param {number} arg.videoHeight - The video height in pixels.
 * @returns {number[]} The geographical coordinates of the projected video point.
 */
export const getTransformatedVideoCoordinates = ({
  geoJSONCoordinates,
  videoCoordinates,
  videoTop,
  videoLeft,
  videoWidth,
  videoHeight
}) => {
  const originPoints = {
    x1: videoLeft,
    y1: videoTop,
    x2: videoLeft,
    y2: videoTop + videoHeight,
    x3: videoLeft + videoWidth,
    y3: videoTop,
    x4: videoLeft + videoWidth,
    y4: videoTop + videoHeight
  };
  const destinationPoints = {
    x1: geoJSONCoordinates[1][0],
    y1: geoJSONCoordinates[1][1],
    x2: geoJSONCoordinates[2][0],
    y2: geoJSONCoordinates[2][1],
    x3: geoJSONCoordinates[0][0],
    y3: geoJSONCoordinates[0][1],
    x4: geoJSONCoordinates[3][0],
    y4: geoJSONCoordinates[3][1]
  };

  const transformationMatrix = calculateHomographyMatrix({ originPoints, destinationPoints });

  const lambda =
    transformationMatrix[6] * videoCoordinates[0] +
    transformationMatrix[7] * videoCoordinates[1] +
    transformationMatrix[8];
  const lng =
    (transformationMatrix[0] * videoCoordinates[0] +
      transformationMatrix[1] * videoCoordinates[1] +
      transformationMatrix[2]) /
    lambda;
  const lat =
    (transformationMatrix[3] * videoCoordinates[0] +
      transformationMatrix[4] * videoCoordinates[1] +
      transformationMatrix[5]) /
    lambda;

  return [lng, lat];
};
